import React, { useMemo } from 'react';
import { Layers, Repeat2, Star, Trophy } from 'lucide-react';
import PracticeSettingsService from '../services/PracticeSettingsService';
import PracticeSummaryProgress, { buildPracticeSummaryCounts } from './PracticeSummaryProgress';
import { t } from '../i18n';
import { formatCompactInteger } from '../utils/numberFormat';
import './NoteStatsPanel.css';

const GRADE_COLORS = {
  1: '#ff4444',
  2: '#ff8844',
  3: '#ffcc44',
  4: '#88cc44',
  5: '#44cc44',
};

function gradeColor(avgGrade) {
  return GRADE_COLORS[Math.round(Math.min(5, Math.max(1, avgGrade)))] || '#999999';
}

function NoteStatsPanel({ flashCards = [], noteName, noteId }) {
  const practiceSummary = useMemo(() => buildPracticeSummaryCounts(flashCards), [flashCards]);

  const summary = useMemo(() => {
    const totalRepeats = flashCards.reduce(
      (sum, card) => sum + PracticeSettingsService.getCardPracticeCount(card),
      0
    );
    const totalScore = flashCards.reduce(
      (sum, card) => sum + PracticeSettingsService.getCardScore(card),
      0
    );
    const avgGrade = flashCards.length
      ? flashCards.reduce(
        (sum, card) => {
          const grade = Number(PracticeSettingsService.getPracticeIntensityLevel(card)) || 0;
          return sum + (grade >= 1 && grade <= 5 ? grade : 1);
        },
        0
      ) / flashCards.length
      : 0;
    const avgRepeats = flashCards.length ? totalRepeats / flashCards.length : 0;
    const avgScore = flashCards.length ? totalScore / flashCards.length : 0;

    return {
      cardCount: flashCards.length,
      totalRepeats,
      avgRepeats,
      avgScore,
      avgGrade,
    };
  }, [flashCards]);

  const label = noteName || noteId || t('learningNavigation.stats.thisDrill');

  return (
    <section className="note-stats-panel" aria-label={t('learningNavigation.stats.accessibleTitle', undefined, { name: label })}>
      <div className="note-stats-summary-grid">
        <div className="note-stats-stat">
          <Layers className="note-stats-stat-icon" size={22} strokeWidth={2.4} aria-hidden="true" />
          <span className="note-stats-stat-value">{summary.cardCount}</span>
          <span className="note-stats-stat-label">{t('learningNavigation.stats.cards')}</span>
        </div>
        <div className="note-stats-stat">
          <Repeat2 className="note-stats-stat-icon" size={22} strokeWidth={2.4} aria-hidden="true" />
          <span className="note-stats-stat-value">{formatCompactInteger(summary.avgRepeats)}</span>
          <span className="note-stats-stat-label">{t('learningNavigation.stats.repeats')}</span>
        </div>
        <div className="note-stats-stat">
          <Trophy className="note-stats-stat-icon" size={22} strokeWidth={2.4} aria-hidden="true" />
          <span className="note-stats-stat-value">{formatCompactInteger(summary.avgScore)}</span>
          <span className="note-stats-stat-label">{t('learningNavigation.stats.score')}</span>
        </div>
        <div className="note-stats-stat">
          <Star className="note-stats-stat-icon" size={22} strokeWidth={2.4} aria-hidden="true" />
          <span
            className="note-stats-stat-value"
            style={{ color: summary.avgGrade > 0 ? gradeColor(summary.avgGrade) : '#9c8f86' }}
          >
            {summary.avgGrade.toFixed(1)}
          </span>
          <span className="note-stats-stat-label">{t('learningNavigation.stats.grade')}</span>
        </div>
      </div>

      <PracticeSummaryProgress counts={practiceSummary} />
    </section>
  );
}

export default NoteStatsPanel;
